import { html, ifDefined } from "../../"

import type {
  HookViewRowCellClass_ReturnType,
  HookViewRowCellClass_Type,
} from "../../global/hooks"
import { HookViewRowCellClass } from "../../global/hooks"

import { SYSBaseElement } from "../sysbaseelement"
import type { CachedRendererData } from "./helpers"
import { _GetAlign, GetRowDisplayValue } from "./helpers"
import type { TableRowDataDef } from "./rowwrapper"
import type { DisplayMode, OvlSysColumn } from "./table"

export type CellClass = {
  className?: string
  tooltip?: string
  draggable?: boolean
}

export let cachedRendererFn: Map<string, CachedRendererData> = new Map<
  string,
  CachedRendererData
>()
export class SYSTableRow extends SYSBaseElement {
  props: any
  row: TableRowDataDef
  hasLazyImage: boolean
  async init() {
    this.row = this.props()
    await super.init()
  }
  async afterRender() {
    if (this.hasLazyImage) {
      let lazyImages = this.querySelectorAll(".ovl-lazy-image")
      lazyImages.forEach((element) => {
        this.row.intersectionObserver.observe(element)
      })
    }
  }
  async getUI() {
    let row = this.row.row
    let def = this.row.tableDef

    let feedback
    let feedbackLevel = def.options.feedback.level
    if (feedbackLevel !== "0:none") {
      let ovlSysState = row["OvlSysState"] as OvlSysColumn
      let fnFeedback = ovlSysState.fnFeedback
      let feedBackErrorCount = 0
      let feedBackWarningCount = 0
      let feedBackInformationCount = 0
      let feedBackSuccessCount = 0
      Object.keys(fnFeedback).forEach((fnKey) => {
        switch (fnFeedback[fnKey].type) {
          case "error":
            feedBackErrorCount++
            break
          case "information":
            feedBackInformationCount++
            break
          case "success":
            feedBackSuccessCount++
            break
          case "warning":
            feedBackWarningCount++
            break
        }
      })
      let feedbackLevelInt = parseInt(feedbackLevel.split(":")[0])
      let btnid = `ovlfeedbackbtn_${this.row.tableDef.id}_${this.row.key}`
      if (
        feedbackLevelInt >= 3 &&
        feedBackInformationCount > 0 &&
        feedBackSuccessCount >= 0 &&
        feedBackErrorCount === 0 &&
        feedBackWarningCount === 0
      ) {
        // if there is only information and maybe a success we display information icon
        feedback = html`<button
          id="${btnid}"
          class="fd-button ovl-rowfnhintbutton __information"
        >
          <i class="sap-icon--message-information"></i>
        </button>`
      } else if (
        feedbackLevelInt > 3 &&
        feedBackSuccessCount > 0 &&
        feedBackErrorCount === 0 &&
        feedBackInformationCount === 0 &&
        feedBackWarningCount === 0
      ) {
        // if there is only success we display success icon
        feedback = html`<button
          id="${btnid}"
          class="fd-button ovl-rowfnhintbutton __success"
        >
          <i class="sap-icon--message-success"></i>
        </button>`
      } else if (
        feedbackLevelInt >= 2 &&
        feedBackWarningCount > 0 &&
        feedBackErrorCount === 0
      ) {
        // if we have warnings and no errors display warning icon
        feedback = html`<button
          id="${btnid}"
          class="fd-button ovl-rowfnhintbutton __warning"
        >
          <i class="sap-icon--message-warning"></i>
        </button>`
      } else if (feedbackLevelInt >= 1 && feedBackErrorCount > 0) {
        // if we have errors display error icon
        feedback = html`<button
          id="${btnid}"
          class="fd-button ovl-rowfnhintbutton __error"
        >
          <i class="sap-icon--message-error"></i>
        </button>`
      }
    }

    if (def.uiState.currentlyAddingKey === this.row.key) {
      return html`<!---->`
    }
    let columns = def.columns

    let visibleColumns = def.uiState.columnsRenderingState.visibleInTable

    // see if we can gbet custom class names for the row columns
    // eg. to color a cell
    // this will also be used for detailview, differentiating by param isDetail
    let customRowCellClasses: HookViewRowCellClass_ReturnType
    let functionName = HookViewRowCellClass.replace("{subFeature}", "")
    let fn = this.actions.app.features[def.feature]
    if (fn?.hooks?.[functionName]) {
      customRowCellClasses = fn.hooks[functionName](<HookViewRowCellClass_Type>{
        columns: def.columns,
        row,
        rowKey: this.row.key,
        displayMode: <DisplayMode>"Table",
        feature: def.feature,
        tableDefId: def.id,
        def,
      })
    }
    let isFirstCol = true
    return html`
      ${visibleColumns.map((k) => {
        let rowFnFeedbackHint
        if (isFirstCol && feedback) {
          rowFnFeedbackHint = feedback
        }
        isFirstCol = false

        let customRowCellClass: string = ""
        let draggable =
          customRowCellClasses?.[k]?.draggable === true ? true : undefined
        let tooltip = customRowCellClasses?.[k]?.tooltip
        if (!tooltip) {
          tooltip = undefined
        }
        if (customRowCellClasses?.[k]?.className) {
          customRowCellClass = customRowCellClasses[k].className
        }

        let col = columns[k]
        let rowPart = GetRowDisplayValue(
          def,
          k,
          this.row.row,
          this.row.key,
          "Table"
        )

        let tdclass = ""
        if (col.ui.expandTableCellOnClick) {
          tdclass = "ovl-expand-onclick"
        }

        // needs to be ignored to get css white-space: line-wrap work correctly
        // prettier-ignore
        return html`
          <td title=${ifDefined(tooltip)} draggable=${ifDefined(draggable)} data-col=${k} class=${`fd-table__cell ${_GetAlign(col.ui.align)} ovltvc ${this.row.thHeaderEnd[k] ? "thGrp" : ""} col__${k} ctl__${col.control} tpe__${columns[k].type} ${customRowCellClass}`}><div class="ovl-td ${tdclass}">${rowPart}${rowFnFeedbackHint}</div></td>
        `
      })}
    `
  }
}
